/**
 * GeneralTab - Pestaña de identificación y ubicación
 *
 * @version 2.0
 * @updated 2026-02-18
 * @module core/map-tools/TreeDetailsPanel
 */

import { useState, useEffect, useRef } from 'react';
import { Copy, Check, ChevronDown, Plus, X } from 'lucide-react';
import type { Tree } from '../../tree-types';
import { FormLabel } from '../FormLabel';
import { speciesApi, type Species, type CreateSpeciesDto } from '../../species-api';

export interface GeneralTabProps {
  tree: Tree;
  onChange: (field: keyof Tree, value: any) => void;
}

export function GeneralTab({ tree, onChange }: GeneralTabProps): JSX.Element {
  const [copied, setCopied] = useState<boolean>(false);

  // Estado del catálogo de especies
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [speciesLoading, setSpeciesLoading] = useState<boolean>(false);
  const [speciesError, setSpeciesError] = useState<string | null>(null);

  // Estado del formulario de nueva especie
  const [showAddSpecies, setShowAddSpecies] = useState<boolean>(false);
  const [newSpecies, setNewSpecies] = useState<CreateSpeciesDto>({
    commonName: '',
    scientificName: '',
    botanicalFamily: '',
    origin: undefined,
  });
  const [savingSpecies, setSavingSpecies] = useState<boolean>(false);
  const [saveSpeciesError, setSaveSpeciesError] = useState<string | null>(null);

  const addSpeciesSectionRef = useRef<HTMLDivElement>(null);

  // Cargar catálogo de especies al montar
  useEffect(() => {
    const load = async () => {
      setSpeciesLoading(true);
      setSpeciesError(null);
      try {
        const data = await speciesApi.getAll();
        setSpeciesList(data);
      } catch {
        setSpeciesError('No se pudo cargar el catálogo de especies');
      } finally {
        setSpeciesLoading(false);
      }
    };
    void load();
  }, []);

  // Scroll al formulario de nueva especie cuando se abre
  useEffect(() => {
    if (showAddSpecies && addSpeciesSectionRef.current) {
      setTimeout(() => {
        addSpeciesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [showAddSpecies]);

  /**
   * Copia las coordenadas al portapapeles
   */
  const handleCopyCoordinates = async (): Promise<void> => {
    const coordinatesText = `${tree.coordinates[0]}, ${tree.coordinates[1]}`;
    try {
      await navigator.clipboard.writeText(coordinatesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error al copiar coordenadas:', error);
    }
  };

  /**
   * Maneja la selección de especie desde el dropdown
   * - Si se elige "add-new": abre formulario y no modifica campos
   * - Si se elige una especie: auto-rellena los campos relacionados
   * - Si se limpia: borra los campos de la especie
   */
  const handleSpeciesChange = (selectedId: string): void => {
    if (selectedId === 'add-new') {
      setShowAddSpecies(true);
      return;
    }

    if (!selectedId) {
      // Limpiar especie seleccionada
      onChange('speciesId', undefined);
      onChange('commonName', undefined);
      onChange('scientificName', undefined);
      onChange('botanicalFamily', undefined);
      onChange('origin', '');
      return;
    }

    const selected = speciesList.find((s) => s.id === selectedId);
    if (!selected) return;

    // Auto-rellenar campos desde la especie seleccionada
    onChange('speciesId', selected.id);
    onChange('commonName', selected.commonName);
    onChange('scientificName', selected.scientificName);
    onChange('botanicalFamily', selected.botanicalFamily ?? undefined);
    onChange('origin', selected.origin ?? '');
  };

  /**
   * Guarda la nueva especie en el catálogo global y la selecciona
   */
  const handleSaveNewSpecies = async (): Promise<void> => {
    if (!newSpecies.commonName.trim() || !newSpecies.scientificName.trim()) {
      setSaveSpeciesError('El nombre común y el nombre científico son obligatorios');
      return;
    }

    setSavingSpecies(true);
    setSaveSpeciesError(null);

    try {
      const dto: CreateSpeciesDto = {
        commonName: newSpecies.commonName.trim(),
        scientificName: newSpecies.scientificName.trim(),
        botanicalFamily: newSpecies.botanicalFamily?.trim() || undefined,
        origin: newSpecies.origin || undefined,
      };

      const created = await speciesApi.create(dto);

      // Agregar a la lista local y seleccionarla
      setSpeciesList((prev) =>
        [...prev, created].sort((a, b) => a.commonName.localeCompare(b.commonName)),
      );

      // Seleccionar la especie recién creada
      onChange('speciesId', created.id);
      onChange('commonName', created.commonName);
      onChange('scientificName', created.scientificName);
      onChange('botanicalFamily', created.botanicalFamily ?? undefined);
      onChange('origin', created.origin ?? '');

      // Cerrar y limpiar formulario
      setShowAddSpecies(false);
      setNewSpecies({ commonName: '', scientificName: '', botanicalFamily: '', origin: undefined });
    } catch (err: any) {
      const msg: string =
        err?.response?.data?.message || 'Error al guardar la especie. Intente nuevamente.';
      setSaveSpeciesError(msg);
    } finally {
      setSavingSpecies(false);
    }
  };

  const handleCancelAddSpecies = (): void => {
    setShowAddSpecies(false);
    setSaveSpeciesError(null);
    setNewSpecies({ commonName: '', scientificName: '', botanicalFamily: '', origin: undefined });
  };

  const hasSpeciesSelected = !!tree.speciesId;

  return (
    <div className="tree-tab-panel">
      {/* Sección: Identificación */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Identificación</h3>

        {/* Código único */}
        <div className="tree-form-group">
          <FormLabel tooltip="Código único de identificación del árbol. Se genera automáticamente pero puede editarse.">
            Código único
          </FormLabel>
          <input
            type="text"
            className="tree-form-input"
            value={tree.code || ''}
            onChange={(e) => onChange('code', e.target.value)}
            placeholder="Código auto-generado"
          />
        </div>

        {/* Nombre del árbol — referencia personal del usuario */}
        <div className="tree-form-group">
          <FormLabel tooltip="Denominación de referencia asignada por el usuario a este árbol específico. Ej: 'El tilo de la plaza', 'Árbol esquina norte'.">
            Nombre del árbol
          </FormLabel>
          <input
            type="text"
            className="tree-form-input"
            value={tree.name || ''}
            onChange={(e) => onChange('name', e.target.value || undefined)}
            placeholder="Ej: El tilo de la plaza (opcional)"
          />
        </div>

        {/* Selector de especie (reemplaza "Nombre común") */}
        <div className="tree-form-group">
          <FormLabel tooltip="Especie del árbol seleccionada del catálogo. Al seleccionar, se auto-completan nombre científico y familia botánica.">
            Especie
          </FormLabel>
          <div className="species-select-wrapper">
            <select
              className="tree-form-select species-select"
              value={tree.speciesId || ''}
              onChange={(e) => handleSpeciesChange(e.target.value)}
              disabled={speciesLoading}
            >
              <option value="">
                {speciesLoading ? 'Cargando...' : '— Seleccionar especie —'}
              </option>
              {speciesList.map((sp) => (
                <option key={sp.id} value={sp.id}>
                  {sp.commonName}
                </option>
              ))}
              <option value="add-new" className="species-add-option">
                + Cargar nueva especie
              </option>
            </select>
            <ChevronDown size={14} className="species-select-icon" />
          </div>
          {speciesError && <span className="species-error-msg">{speciesError}</span>}
        </div>

        {/* Formulario expandible: Cargar nueva especie */}
        {showAddSpecies && (
          <div className="add-species-section" ref={addSpeciesSectionRef}>
            <div className="add-species-header">
              <span className="add-species-title">Cargar nueva especie</span>
              <button
                type="button"
                className="add-species-close-btn"
                onClick={handleCancelAddSpecies}
                title="Cancelar"
              >
                <X size={14} />
              </button>
            </div>

            <div className="tree-form-group">
              <FormLabel required tooltip="Nombre vulgar o popular de la especie. Debe ser único en el catálogo.">
                Nombre común
              </FormLabel>
              <input
                type="text"
                className="tree-form-input"
                value={newSpecies.commonName}
                onChange={(e) =>
                  setNewSpecies((prev) => ({ ...prev, commonName: e.target.value }))
                }
                placeholder="Ej: Jacarandá"
              />
            </div>

            <div className="tree-form-group">
              <FormLabel required tooltip="Nombre científico completo en nomenclatura binomial (género + especie).">
                Nombre científico
              </FormLabel>
              <input
                type="text"
                className="tree-form-input"
                value={newSpecies.scientificName}
                onChange={(e) =>
                  setNewSpecies((prev) => ({ ...prev, scientificName: e.target.value }))
                }
                placeholder="Ej: Jacaranda mimosifolia"
              />
            </div>

            <div className="tree-form-row">
              <div className="tree-form-group">
                <FormLabel tooltip="Familia taxonómica de la especie.">
                  Familia botánica
                </FormLabel>
                <input
                  type="text"
                  className="tree-form-input"
                  value={newSpecies.botanicalFamily || ''}
                  onChange={(e) =>
                    setNewSpecies((prev) => ({ ...prev, botanicalFamily: e.target.value }))
                  }
                  placeholder="Ej: Bignoniaceae"
                />
              </div>

              <div className="tree-form-group">
                <FormLabel tooltip="Indica si la especie es nativa o exótica.">
                  Origen
                </FormLabel>
                <select
                  className="tree-form-select"
                  value={newSpecies.origin || ''}
                  onChange={(e) =>
                    setNewSpecies((prev) => ({
                      ...prev,
                      origin: (e.target.value as 'native' | 'exotic') || undefined,
                    }))
                  }
                >
                  <option value="">Seleccionar...</option>
                  <option value="native">Nativa</option>
                  <option value="exotic">Exótica</option>
                </select>
              </div>
            </div>

            {saveSpeciesError && (
              <div className="add-species-error">{saveSpeciesError}</div>
            )}

            <div className="add-species-actions">
              <button
                type="button"
                className="add-species-save-btn"
                onClick={handleSaveNewSpecies}
                disabled={savingSpecies}
              >
                <Plus size={14} />
                {savingSpecies ? 'Guardando...' : 'Guardar especie'}
              </button>
              <button
                type="button"
                className="add-species-cancel-btn"
                onClick={handleCancelAddSpecies}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Nombre científico — read-only cuando especie seleccionada */}
        <div className="tree-form-group">
          <FormLabel tooltip="Nombre científico en nomenclatura binomial. Se auto-completa al seleccionar una especie del catálogo.">
            Nombre científico
          </FormLabel>
          <input
            type="text"
            className={`tree-form-input ${hasSpeciesSelected ? 'species-readonly-field' : ''}`}
            value={tree.scientificName || ''}
            onChange={(e) => !hasSpeciesSelected && onChange('scientificName', e.target.value)}
            readOnly={hasSpeciesSelected}
            placeholder={hasSpeciesSelected ? '' : 'Ej: Jacaranda mimosifolia'}
          />
        </div>

        {/* Familia botánica — read-only cuando especie seleccionada */}
        <div className="tree-form-group">
          <FormLabel tooltip="Familia taxonómica del árbol. Se auto-completa al seleccionar una especie del catálogo.">
            Familia botánica
          </FormLabel>
          <input
            type="text"
            className={`tree-form-input ${hasSpeciesSelected ? 'species-readonly-field' : ''}`}
            value={tree.botanicalFamily || ''}
            onChange={(e) => !hasSpeciesSelected && onChange('botanicalFamily', e.target.value)}
            readOnly={hasSpeciesSelected}
            placeholder={hasSpeciesSelected ? '' : 'Ej: Bignoniaceae'}
          />
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Indica si la especie es autóctona (nativa) de la región o introducida (exótica).">
              Origen
            </FormLabel>
            <select
              className="tree-form-select"
              value={tree.origin || ''}
              onChange={(e) => onChange('origin', e.target.value)}
            >
              <option value="">Seleccionar...</option>
              <option value="native">Nativo</option>
              <option value="exotic">Exótico</option>
            </select>
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Edad aproximada del árbol en años desde su plantación o germinación.">
              Edad estimada (años)
            </FormLabel>
            <input
              type="number"
              className="tree-form-input"
              value={tree.estimatedAge || ''}
              onChange={(e) => onChange('estimatedAge', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="0"
              min="0"
            />
          </div>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Fecha exacta o aproximada en que el árbol fue plantado en su ubicación actual.">
            Fecha de plantación
          </FormLabel>
          <input
            type="date"
            className="tree-form-input"
            value={tree.plantingDate ? tree.plantingDate.toISOString().split('T')[0] : ''}
            onChange={(e) => onChange('plantingDate', e.target.value ? new Date(e.target.value) : undefined)}
          />
        </div>
      </div>

      {/* Sección: Ubicación */}
      <div className="tree-form-section">
        <h3 className="tree-form-section-title">Ubicación</h3>

        <div className="tree-form-row tree-coordinates-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Coordenada de longitud (Este-Oeste) en grados decimales. Se captura automáticamente al hacer clic en el mapa.">
              Longitud
            </FormLabel>
            <input
              type="number"
              className="tree-form-input"
              value={tree.coordinates[0]}
              onChange={(e) => onChange('coordinates', [parseFloat(e.target.value), tree.coordinates[1]])}
              step="0.000001"
            />
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Coordenada de latitud (Norte-Sur) en grados decimales. Se captura automáticamente al hacer clic en el mapa.">
              Latitud
            </FormLabel>
            <input
              type="number"
              className="tree-form-input"
              value={tree.coordinates[1]}
              onChange={(e) => onChange('coordinates', [tree.coordinates[0], parseFloat(e.target.value)])}
              step="0.000001"
            />
          </div>

          <button
            type="button"
            className="tree-copy-coords-btn"
            onClick={handleCopyCoordinates}
            title={copied ? 'Copiado!' : 'Copiar coordenadas'}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Nombre de la calle, avenida o espacio público donde se encuentra ubicado el árbol.">
            Dirección / Calle
          </FormLabel>
          <input
            type="text"
            className="tree-form-input"
            value={tree.address || ''}
            onChange={(e) => onChange('address', e.target.value)}
            placeholder="Ej: Av. Libertador"
          />
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Calles que delimitan la cuadra donde se encuentra el árbol (ej: entre Calle San Martín y Calle Belgrano).">
            Entre calles
          </FormLabel>
          <input
            type="text"
            className="tree-form-input"
            value={tree.betweenStreets || ''}
            onChange={(e) => onChange('betweenStreets', e.target.value)}
            placeholder="Ej: Calle 1 - Calle 2"
          />
        </div>

        <div className="tree-form-row">
          <div className="tree-form-group">
            <FormLabel tooltip="Número de puerta o propiedad más cercano al árbol para facilitar su localización.">
              Número más cercano
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.nearestNumber || ''}
              onChange={(e) => onChange('nearestNumber', e.target.value)}
              placeholder="1234"
            />
          </div>

          <div className="tree-form-group">
            <FormLabel tooltip="Barrio, zona o distrito administrativo donde se encuentra el árbol.">
              Barrio / Zona
            </FormLabel>
            <input
              type="text"
              className="tree-form-input"
              value={tree.neighborhood || ''}
              onChange={(e) => onChange('neighborhood', e.target.value)}
              placeholder="Centro, Palermo, etc."
            />
          </div>
        </div>

        <div className="tree-form-group">
          <FormLabel tooltip="Tipo de espacio urbano donde está plantado el árbol (vereda, plaza, parque, etc.).">
            Tipo de emplazamiento
          </FormLabel>
          <select
            className="tree-form-select"
            value={tree.locationType || ''}
            onChange={(e) => onChange('locationType', e.target.value)}
          >
            <option value="">Seleccionar...</option>
            <option value="sidewalk">Vereda</option>
            <option value="square">Plaza</option>
            <option value="park">Parque</option>
            <option value="median">Cantero central</option>
            <option value="roundabout">Rotonda</option>
            <option value="other">Otro</option>
          </select>
        </div>
      </div>
    </div>
  );
}
